import { Button, cn, Input, Label, Modal } from '@crm/ui';
import { ChevronRight, Plus, Search, Ticket } from 'lucide-react';
import { useState } from 'react';
import { toErrorMessage } from '@/modules/auth/utils';
import { type Coupon, useCreateCoupon } from '@/modules/coupons/api';

export interface CouponDraft {
  code: string;
  percent: string;
}

interface CouponFieldProps {
  /** O cupom ja preso na campanha, quando existe. */
  attached: Coupon | undefined;
  /** Os cupons percentuais que a empresa ja tem. */
  available: Coupon[];
  draft: CouponDraft;
  onDraftChange: (draft: CouponDraft) => void;
  onPick: (couponId: string | null) => void;
  /** O codigo que o objetivo sugere, para a janela de criar ja vir escrita. */
  suggestion: CouponDraft;
  readOnly: boolean;
}

/**
 * O desconto da campanha, junto do objetivo que o sugere.
 *
 * Fica aqui, e nao na mensagem, porque a oferta e parte da decisao: o texto
 * so pode falar do cupom depois que ele existe.
 *
 * Criar e reaproveitar sao caminhos diferentes, e cada um mora na sua janela.
 * Na tela fica so o resultado - o cupom escolhido, editavel, com os dois
 * botoes embaixo para trocar de ideia. Os dois caminhos abertos ao mesmo
 * tempo, como estavam antes, nao deixavam ver qual dos dois estava valendo.
 */
export function CouponField({
  attached,
  available,
  draft,
  onDraftChange,
  onPick,
  suggestion,
  readOnly,
}: CouponFieldProps) {
  const [criando, setCriando] = useState(false);
  const [buscando, setBuscando] = useState(false);

  return (
    <>
      <div className="space-y-6">
        <Label>Cupom da campanha</Label>

        <div className="space-y-5">
          {attached ? (
            <div className="flex flex-wrap items-end gap-x-6 gap-y-4">
              <div className="space-y-2">
                <Label htmlFor="campanha-cupom-codigo">Codigo</Label>
                <Input
                  id="campanha-cupom-codigo"
                  className="w-44 uppercase"
                  disabled={readOnly}
                  value={draft.code}
                  onChange={(event) =>
                    onDraftChange({ ...draft, code: event.target.value.toUpperCase() })
                  }
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="campanha-cupom-valor">Desconto</Label>
                <div className="flex items-center gap-2">
                  <Input
                    id="campanha-cupom-valor"
                    type="number"
                    inputMode="numeric"
                    min={1}
                    max={100}
                    className="w-20"
                    disabled={readOnly}
                    value={draft.percent}
                    onChange={(event) => onDraftChange({ ...draft, percent: event.target.value })}
                  />
                  <span className="text-sm text-muted-foreground">%</span>
                </div>
              </div>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">
              Nenhum cupom ainda. Crie um, ou aproveite um que a empresa ja tem.
            </p>
          )}

          <div className="flex flex-wrap gap-2">
            <Button variant="outline" disabled={readOnly} onClick={() => setCriando(true)}>
              <Plus className="h-4 w-4" />
              Criar cupom
            </Button>
            <Button
              variant="outline"
              disabled={readOnly || available.length === 0}
              onClick={() => setBuscando(true)}
            >
              <Search className="h-4 w-4" />
              Pesquisar cupom
            </Button>
          </div>
        </div>
      </div>

      {criando ? (
        <CreateCouponModal
          suggestion={suggestion}
          onClose={() => setCriando(false)}
          onCreated={(id) => {
            setCriando(false);
            onPick(id);
          }}
        />
      ) : null}

      {buscando ? (
        <SearchCouponModal
          available={available}
          attachedId={attached?.id ?? null}
          onClose={() => setBuscando(false)}
          onPick={(id) => {
            setBuscando(false);
            onPick(id);
          }}
        />
      ) : null}
    </>
  );
}

/** Cria o cupom na hora: quem abriu esta janela ja decidiu que quer um. */
function CreateCouponModal({
  suggestion,
  onClose,
  onCreated,
}: {
  suggestion: CouponDraft;
  onClose: () => void;
  onCreated: (couponId: string) => void;
}) {
  const createCoupon = useCreateCoupon();
  const [codigo, setCodigo] = useState(suggestion.code);
  const [percentual, setPercentual] = useState(suggestion.percent);

  const valor = Number(percentual);
  const valido = codigo.trim() !== '' && Number.isFinite(valor) && valor > 0 && valor <= 100;

  async function cria() {
    if (!valido) return;

    const novo = await createCoupon.mutateAsync({
      code: codigo.trim(),
      description: 'Desconto da campanha',
      type: 'PERCENT',
      value: valor,
      serviceIds: [],
      isActive: true,
    });

    onCreated(novo.id);
  }

  return (
    <Modal
      open
      title="Criar cupom"
      size="small"
      onClose={onClose}
      actions={[
        { label: 'Cancelar', variant: 'ghost', onClick: onClose },
        {
          label: 'Criar',
          onClick: () => void cria(),
          disabled: !valido || createCoupon.isPending,
          loading: createCoupon.isPending,
        },
      ]}
    >
      <div className="space-y-5">
        <div className="space-y-2">
          <Label htmlFor="novo-cupom-codigo">Codigo</Label>
          <Input
            id="novo-cupom-codigo"
            className="uppercase"
            value={codigo}
            onChange={(event) => setCodigo(event.target.value.toUpperCase())}
          />
          <p className="text-xs text-muted-foreground">E ele que o cliente le e digita.</p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="novo-cupom-valor">Desconto</Label>
          <div className="flex items-center gap-2">
            <Input
              id="novo-cupom-valor"
              type="number"
              inputMode="numeric"
              min={1}
              max={100}
              className="w-24"
              value={percentual}
              onChange={(event) => setPercentual(event.target.value)}
            />
            <span className="text-sm text-muted-foreground">%</span>
          </div>
        </div>

        {createCoupon.error ? (
          <p className="text-sm text-destructive">{toErrorMessage(createCoupon.error)}</p>
        ) : null}
      </div>
    </Modal>
  );
}

/** Os cupons que a empresa ja tem, para nao nascer o quarto "VOLTA10". */
function SearchCouponModal({
  available,
  attachedId,
  onClose,
  onPick,
}: {
  available: Coupon[];
  attachedId: string | null;
  onClose: () => void;
  onPick: (couponId: string) => void;
}) {
  const [busca, setBusca] = useState('');
  const termo = busca.trim().toLowerCase();
  const lista = termo
    ? available.filter((coupon) => coupon.code.toLowerCase().includes(termo))
    : available;

  return (
    <Modal open title="Pesquisar cupom" size="small" onClose={onClose}>
      <div className="space-y-4">
        <Input
          placeholder="Buscar por codigo..."
          value={busca}
          onChange={(event) => setBusca(event.target.value)}
        />

        {lista.length === 0 ? (
          <p className="text-sm text-muted-foreground">Nenhum cupom com esse codigo.</p>
        ) : (
          <ul className="space-y-1">
            {lista.map((coupon) => (
              <li key={coupon.id}>
                {/* Borda e seta a direita: sem isso a linha parecia um resumo, e
                    nao algo em que se clica. */}
                <button
                  type="button"
                  onClick={() => onPick(coupon.id)}
                  className={cn(
                    'flex w-full items-center gap-3 rounded-lg border p-3 text-left transition-colors',
                    coupon.id === attachedId
                      ? 'border-primary bg-primary/10'
                      : 'border-border hover:bg-muted/60',
                  )}
                >
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/15 text-primary">
                    <Ticket className="h-4 w-4" />
                  </span>
                  <span className="min-w-0 flex-1 truncate font-medium">{coupon.code}</span>
                  <span className="text-sm text-muted-foreground">{coupon.value}%</span>
                  <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Modal>
  );
}
